import React, { useMemo } from 'react';

import { colors, typography } from '@pray/shared/components/foundations';
import Text from '@pray/shared/components/ui/Text';
import { formatCurrency } from '@pray/shared/utils';

import { amountTiers, donationIntervals, intervalAbbr } from '../../../constants';
import DonationOption from '../../../DonationOption/DonationOption';
import type { DonationForm, Fund } from '../../../types';

import styles from './DonationOptions.module.scss';

type DonationOptionsProps = {
  form: DonationForm;
  fund: Fund;
  showCustomAmountButton: boolean;
  onCustomAmountSelect: () => void;
};

export default function DonationOptions({
  form,
  fund,
  showCustomAmountButton,
  onCustomAmountSelect,
}: DonationOptionsProps) {
  const subtitle = '1 Ads Free Subscription';

  const donationOptions = useMemo(() => {
    const options = [
      {
        isFeatured: true,
        value: amountTiers.tier1.value,
        title: amountTiers.tier1.label,
        subtitle,
        amountByInterval: {
          month: fund.default_amount_monthly,
          year: fund.default_amount_yearly,
        },
      },
    ];

    // Only include second option if showCustomAmountButton is false
    if (!showCustomAmountButton) {
      options.push({
        isFeatured: false,
        value: amountTiers.tier2.value,
        title: amountTiers.tier2.label,
        subtitle,
        amountByInterval: {
          month: fund.amount_monthly_2,
          year: fund.amount_yearly_2,
        },
      });
    }

    return options;
  }, [fund, showCustomAmountButton]);

  return (
    <div className={styles.optionsContainer}>
      {donationOptions.map((option) => (
        <DonationOption
          key={option.value}
          name={option.value}
          isFeatured={option.isFeatured}
          isSelected={form.values.amountTier === option.value}
          onChange={(amountTier) => {
            form.setValue('amountTier', amountTier);

            const isTier1Selected = amountTier === amountTiers.tier1.value;

            if (form.values.interval === donationIntervals.month) {
              const amount = isTier1Selected ? fund.default_amount_monthly : fund.amount_monthly_2;
              form.setValue('amount', amount);
            }

            if (form.values.interval === donationIntervals.year) {
              const amount = isTier1Selected ? fund.default_amount_yearly : fund.amount_yearly_2;
              form.setValue('amount', amount);
            }
          }}
        >
          <div className={styles.optionContainer}>
            <div className={styles.optionRow1}>
              <Text className={styles.optionTitle} variant={typography.heading_md}>
                {option.title}
              </Text>
              {option.amountByInterval && (
                <div className={styles.optionAmount}>
                  <Text variant={typography.heading_md}>
                    {formatCurrency(option.amountByInterval[form.values.interval])}
                    <Text variant={typography.heading_md} color={colors.text_secondary}>
                      /{intervalAbbr[form.values.interval] || form.values.interval}
                    </Text>
                  </Text>
                </div>
              )}
            </div>
            {!fund.default && (
              <div className={styles.optionRow2}>
                <Text className={styles.optionSubtitle} variant={typography.heading_md} color={colors.text_secondary}>
                  {option.subtitle}
                </Text>
              </div>
            )}
          </div>
        </DonationOption>
      ))}
      {showCustomAmountButton && (
        <DonationOption name="custom" isSelected={false} isFeatured={false} onChange={onCustomAmountSelect}>
          <Text className={styles.optionTitle} variant={typography.heading_md}>
            Enter Custom Amount
          </Text>
        </DonationOption>
      )}
    </div>
  );
}
